import { useState, useRef, useEffect } from 'react';
import { Menu, Drawer, Dropdown } from 'antd';
import { Link } from 'react-router-dom';
import { MenuOutlined, UserOutlined, LogoutOutlined, DownOutlined, MoreOutlined } from '@ant-design/icons';
import { FiUser,FiLogOut } from "react-icons/fi";
import { ChevronDown } from 'lucide-react';
import DropdownNotification from './dashboard/DropdownNotification';
import Cookies from 'js-cookie';
import { useAuth } from '../providers/AuthProvider';
import menuItems, { MenuItem, SubMenuItem } from '../config/menuItems';
import type { MenuProps } from 'antd';
import { usePermission } from '../permission/PermissionContext';
import {SALES_URL, APP_DOMAIN} from '../env'

const getInitials = (name: string): string => {
  return name
    .split(' ') // split by space
    .filter(Boolean) // remove empty strings
    .slice(0, 2) // take first two words
    .map((word) => word[0]) // take first char of each
    .join('')
    .toUpperCase();
};

const getFilteredMenuItems = (items: MenuItem[]): MenuItem[] => {
  return items.filter(item => {
    if (item.permission && !usePermission(item.permission)) {
      return false; // User doesn't have permission, hide this item
    }

    // Check subMenu items if they exist
    if (item.subMenu) {
      item.subMenu = item.subMenu.filter(subItem => {
        return !subItem.permission || usePermission(subItem.permission);
      });
    }

    return true; // Keep this menu item if permission is granted
  });
};

export function Header() {
  const { userInfo } = useAuth();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) { // 1024px is the 'lg' breakpoint in Tailwind
        setDrawerVisible(false);
      }
    };
 
    // Add event listener
    window.addEventListener('resize', handleResize);
   
    // Also check on mount
    handleResize();
 
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  // Convert our menu items to Ant Design menu items format
  const convertToAntMenuItems = (items: MenuItem[]): MenuProps['items'] => {
    return items.map((item, index) => {
      const key = String(index + 1);
      const IconComponent = item.icon;
      
      if (item.subMenu) {
        // This is a submenu item - add arrow back for main menu
        return {
          key,
          icon: (
            <span>
              <IconComponent />
            </span>
          ),
          label: (
            <span>
              {item.label} <ChevronDown style={{  display: 'inline-block', width: '14px' }} />
            </span>
          ),
          children: convertSubMenuItems(item.subMenu)
        };
      }
      
      // This is a regular menu item
      return {
        key,
        icon: (
          <span>
            <IconComponent />
          </span>
        ),
        label: <a href={item.path} target="_self">{item.label}</a>
      };
    });
  };

  // Convert submenu items
  const convertSubMenuItems = (items: SubMenuItem[]): MenuProps['items'] => {
    return items.map((item, index) => {
      const key = `sub-${index + 1}`;
      
      if (item.subMenu) {
        // Nested submenu
        return {
          key,
          label: item.label,
          children: convertSubMenuItems(item.subMenu)
        };
      }
      
      // Regular submenu item
      return {
        key,
        label: <a href={item.path} target="_self">{item.label}</a>
      };
    });
  };

  // Check user permissions and filter menu items
  let permissionMenu = getFilteredMenuItems(menuItems);
  const antMenuItems = convertToAntMenuItems(permissionMenu);
  
  const handleLogout = () => {
    // Remove cookies
    Cookies.remove('access_token', { domain: `.${APP_DOMAIN}.com`, path: '/' });
    
    // Redirect to default URL
    window.location.href = `https://app.${APP_DOMAIN}.com`;
  };

  const getLogo = () => {
    return '/dbi-logo.png';
  };

  // User dropdown menu items
  const userDropdownItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div className="py-1">
          <div className="text-sm font-bold">{userInfo?.firstName && userInfo?.lastName ? `${userInfo.firstName} ${userInfo.lastName}` : 'Demo User'}</div>
          <div className="text-xs text-gray-500">{userInfo?.email || 'demo@example.com'}</div>
        </div>
      ),

      style: { backgroundColor: 'white', cursor: 'text' },
    },
    {
      type: 'divider',
    },
    {
      key: 'profile',
      label: 'My Profile',
      icon: <UserOutlined className="text-[14px]" />,
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined className="text-[14px]" />,
      onClick: handleLogout,
    },
  ];

  // Handle dropdown menu click
  const handleMenuClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'logout') {
      handleLogout();
    }
  };

  const isDemo = window.location.hostname.includes("demodbi360");
  const dashboardUrl = isDemo
    ? "https://app.demodbi360.com/dashboard"
    : "https://app.dbi360.com/dashboard";

  return (
    <>
      <style>
        {`
          .ant-menu-submenu-open .anticon-down {
            transform: rotate(180deg);
            transition: transform 0.3s ease;
          }
          .anticon-down {
            transition: transform 0.3s ease;
          }
          
          /* Custom styling for More dropdown */
          .ant-menu-overflow-item {
            color: #6c5ffc !important;
          }
          .ant-menu-overflow-item:hover {
            color: #5a4de6 !important;
          }
          
          /* Hide arrows in overflow dropdown */
          .ant-menu-overflow .ant-menu-submenu-arrow,
          .ant-menu-overflow .ant-menu-submenu-expand-icon {
            display: none !important;
          }
          
          /* Active/Selected menu item styling - only for properly selected items */
          .ant-menu-horizontal > .ant-menu-item-selected {
            color: #6c5ffc !important;
            background-color: transparent !important;
            border-bottom: 2px solid #6c5ffc !important;
          }
          
          /* Remove incorrect active styling from non-selected items */
          .ant-menu-horizontal > .ant-menu-item:not(.ant-menu-item-selected) {
            color: #333 !important;
            background-color: transparent !important;
            border-bottom: none !important;
          }
          
          /* Override default ant menu styles */
          .ant-menu-horizontal > .ant-menu-item:hover,
          .ant-menu-horizontal > .ant-menu-submenu:hover {
            color: #6c5ffc !important;
          }
          
          /* Ensure submenu items are not incorrectly styled as active */
          .ant-menu-horizontal > .ant-menu-submenu > .ant-menu-submenu-title {
            color: #333 !important;
            background-color: transparent !important;
            border-bottom: none !important;
          }
            .ant-menu-submenu-arrow{
              display:none;
            }
        `}
      </style>
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white drop-shadow dark:bg-boxdark dark:drop-shadow-none py-2 px-4">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between">
        <button
          onClick={() => setDrawerVisible(true)}
          className="text-gray-500 hover:text-gray-700"
        >
          <MenuOutlined className="text-xl" />
        </button>
        <a href={dashboardUrl} target="_blank"  className="absolute left-1/2 transform -translate-x-1/2">
          <img 
            src={getLogo()} 
            alt="Logo" 
            className="inline-block w-10 " 
          />
        </a>
        <div className="flex justify-between gap-3">
          <ul className="flex items-center">
            <DropdownNotification />
          </ul>
          <div className="w-28 h-10 flex items-center justify-center">
            {userInfo?.logo_url ? (
              <img
                src={userInfo.logo_url}
                alt={userInfo.companyName}
                className="w-full h-full object-contain"
              />
            ) : (
              <span className="text-sm font-medium text-gray-600">
                {getInitials(userInfo?.companyName || 'NA')}
              </span>
            )}
          </div>
          <div className="relative" ref={dropdownRef}>
            <Dropdown
              menu={{ items: userDropdownItems, onClick: handleMenuClick }}
              placement="bottomRight"
              trigger={['click']} 
              dropdownRender={(menu) => (
                <div style={{ width: '250px', boxShadow: '0 3px 6px rgba(0,0,0,0.16)' }}>
                  {menu}
                </div>
              )}
            >
              <button className="h-10 w-10 rounded-full bg-[#6762ea] flex items-center justify-center text-white font-semibold text-lg">
                {userInfo?.firstName && userInfo?.lastName ?
                  `${userInfo.firstName[0]}${userInfo.lastName[0]}`.toUpperCase() :
                  'U'
                }
              </button>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:flex items-center justify-between">
        <div className="flex items-center flex-1" style={{ width: '100%', overflow: 'hidden' }}>
          <a href={dashboardUrl} target="_blank" >
            <img 
              src={getLogo()} 
              alt="Logo" 
              className="w-10 object-contain"
            />
          </a>

          <Menu
            mode="horizontal"
            className="flex-1 border-0 ms-10"
            items={antMenuItems}
            defaultSelectedKeys={['1']} // Keep Dashboard (first item) as default selected
            style={{ maxWidth: '70%' }}
            overflowedIndicator={
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>More</span>
                <ChevronDown style={{ width: '14px' }} />
              </span>
            }
          />
        </div>
        <div className="flex items-center gap-4">
          <ul className="flex items-center ">
            <DropdownNotification />
          </ul>
          <div className="w-28 h-10 flex items-center justify-center">
            {userInfo?.logo_url ? (
              <img
                src={userInfo.logo_url}
                alt={userInfo.companyName}
                className="w-full h-full object-contain"
              />
            ) : (
              <span className="text-sm font-medium text-gray-600">
                {getInitials(userInfo?.companyName || 'NA')}
              </span>
            )}
          </div>
          <div className="relative" ref={dropdownRef}>
            <Dropdown
              menu={{ items: userDropdownItems, onClick: handleMenuClick }}
              placement="bottomRight"
              trigger={['click']}
              overlayStyle={{ marginTop: '70px' }}
              dropdownRender={(menu) => (
                <div style={{ width: '250px', boxShadow: '0 3px 6px rgba(0,0,0,0.16)' }}>
                  {menu}
                </div>
              )}
            >
              <button className="h-10 w-10 rounded-full bg-[#6762ea] flex items-center justify-center text-white font-semibold text-lg">
                {userInfo?.firstName && userInfo?.lastName ?
                  `${userInfo.firstName[0]}${userInfo.lastName[0]}`.toUpperCase() :
                  'U'
                }
              </button>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <Drawer
        title="Menu"
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        className="lg:hidden"
      >
        <Menu
          mode="vertical"
          items={antMenuItems}
          defaultSelectedKeys={['1']} // Keep Dashboard (first item) as default selected
          style={{ border: 'none' }}
          triggerSubMenuAction="click" 
        />
      </Drawer>
      </header>
    </>
  );
}